import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import useWidgetData from '../hooks/useWidgetData';
import useWidgetConfig from '../hooks/useWidgetConfig';
import WidgetEmptyState from '../WidgetEmptyState';
import _ from 'underscore';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { TableComponent } from '../../TableComponent_v2';

// Pre-define icons at module level (babel macro requires static values)
const serverIcon = icon({ name: 'server', family: 'sharp', style: 'light' });
const folderOpenIcon = icon({ name: 'folder-open', family: 'sharp', style: 'light' });
const filterIcon = icon({ name: 'filter', family: 'sharp', style: 'light' });


/**
 * ResourceListWidget - Shows resource list in a table format
 * Supports two display levels:
 * - First level: Shows only resource clusters with total number of resources
 * - Second level: Shows all details, optionally filtered by selected resource clusters
 */
const ResourceListWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false
}) => {
  // Use centralized config hook for date/filter handling
  const { finalConfig, stableFilters } = useWidgetConfig(config, filters, {
    includeDates: true
  });

  // Column visibility state - get from config or use defaults
  const visibleColumns = useMemo(() => {
    const defaultColumns = {
      vendor: true,
      type: true,
      units: true,
      status: true,
      cost: true,
      cpu: true,
      memory: true,
      storage: true,
    };

    if (config?.visibleColumns) {
      return { ...defaultColumns, ...config.visibleColumns };
    }

    return defaultColumns;
  }, [config?.visibleColumns]);

  // Column order (draggable in widget config)
  const optionalColumnOrder = useMemo(() => {
    const defaultOrder = ["vendor", "type", "units", "status", "cost", "cpu", "memory", "storage"];
    if (Array.isArray(config?.visibleColumnsOrder) && config.visibleColumnsOrder.length > 0) {
      // preserve only known columns
      return config.visibleColumnsOrder.filter((col) => defaultOrder.includes(col));
    }
    return defaultOrder;
  }, [config?.visibleColumnsOrder]);

  const { data, isLoading, error } = useWidgetData(finalConfig, stableFilters);

  // Transform data if it comes from API
  const rawData = useMemo(() => data?.ResourceList || data?.data || [], [data]);

  // Group resource list by a field (similar to ResourceList.jsx)
  const groupResourceList = useCallback((data, groupBy) => {
    const groupedAssets = [];
    let lastGroupBy = "";
    let lastGroup = {};

    let pdata = _.clone(data);

    pdata.sort((a, b) => {
      // Special case: if either is "Other", handle differently
      if (a[groupBy] === "Other") return 1;  // Other goes last
      if (b[groupBy] === "Other") return -1; // Other goes last
      
      // Normal alphabetical comparison for non-Other groups
      if (a[groupBy] < b[groupBy]) {
        return -1;
      }
      if (a[groupBy] > b[groupBy]) {
        return 1;
      }
      return 0;
    });

    for (const asset of pdata) {
      if (asset[groupBy] !== lastGroupBy) {
        lastGroupBy = asset[groupBy];
        lastGroup = {
          [groupBy]: asset[groupBy],
          units: 0,
          cost: 0,
          insights: 0,
          subRows: []
        };
        groupedAssets.push(lastGroup);
      }

      lastGroup.units += asset.units;

      if (asset.cost > -1) {
        lastGroup.cost += asset.cost;
      }

      if (asset.insights > -1) {
        lastGroup.insights += asset.insights;
      }

      lastGroup.subRows.push(asset);
    }

    return groupedAssets;
  }, []);

  // Prepare data - transform to format expected by TableComponent (with grouping field)
  const preparedData = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return [];
    }

    const displayLevel = config?.displayLevel || 'first';
    const selectedClusters = config?.selectedClusters || [];

    // First, group by resource_cluster
    const resourceList = groupResourceList(rawData, "resource_cluster");

    let transformedData = [];

    if (displayLevel === 'first') {
      // First level: Show only clusters with totals
      transformedData = resourceList.map(cluster => ({
        grouping: cluster.resource_cluster, // TableComponent expects 'grouping' field
        resource_cluster: cluster.resource_cluster,
        resource_name: cluster.resource_cluster,
        resource_type: '',
        vendor_name: '',
        vendor: '', // Map to vendor measure
        type: '', // Map to type measure
        units: cluster.units,
        resource_status: '',
        status: '', // Map to status measure
        cost: cluster.cost,
        utilization_cpu: null,
        cpu: null, // Map to cpu measure
        utilization_memory: null,
        memory: null, // Map to memory measure
        utilization_storage: null,
        storage: null, // Map to storage measure
        has_cpu_metrics: false,
        has_memory_metrics: false,
        has_storage_metrics: false,
        isGroup: true
      }));
    } else {
      // Second level: Show only details (subRows), no cluster rows visible
      if (!selectedClusters || selectedClusters.length === 0) {
        return [];
      }

      // Handle "Other" cluster specially (group by resource_type)
      const otherList = resourceList.filter(resource => resource.resource_cluster === "Other");
      const nonOtherList = resourceList.filter(resource => resource.resource_cluster !== "Other");

      let allClusters = [...nonOtherList];

      if (otherList.length > 0) {
        const otherResourceList = groupResourceList(otherList[0].subRows, "resource_type");
        allClusters.push({
          resource_cluster: "Other",
          units: otherList[0].units,
          cost: otherList[0].cost,
          insights: otherList[0].insights,
          subRows: otherResourceList
        });
      }

      // Filter by selected clusters
      const clustersToShow = allClusters.filter(cluster => selectedClusters.includes(cluster.resource_cluster));

      // Get selected resource types for "Other" cluster
      const selectedOtherResourceTypes = config?.selectedOtherResourceTypes || [];

      // Flatten: Extract all subRows from clusters to show only details
      const flattenedDetails = [];
      for (const cluster of clustersToShow) {
        if (cluster.subRows && cluster.subRows.length > 0) {
          if (cluster.resource_cluster === "Other") {
            if (!selectedOtherResourceTypes || selectedOtherResourceTypes.length === 0) {
              continue;
            }
            
            const resourceTypeGroupsToShow = cluster.subRows.filter(rtg => selectedOtherResourceTypes.includes(rtg.resource_type));
            
            for (const resourceTypeGroup of resourceTypeGroupsToShow) {
              if (resourceTypeGroup.subRows && resourceTypeGroup.subRows.length > 0) {
                flattenedDetails.push(...resourceTypeGroup.subRows);
              } else {
                flattenedDetails.push(resourceTypeGroup);
              }
            }
          } else {
            flattenedDetails.push(...cluster.subRows);
          }
        }
      }

      transformedData = flattenedDetails.map(row => ({
        grouping: row.resource_name || row.resource_cluster || '', // TableComponent expects 'grouping' field
        resource_name: row.resource_name,
        resource_cluster: row.resource_cluster,
        resource_type: row.resource_type,
        vendor_name: row.vendor_name,
        vendor: row.vendor_name || '', // Map to vendor measure
        type: row.resource_type || '', // Map to type measure
        units: row.units,
        resource_status: row.resource_status,
        status: row.resource_status || '', // Map to status measure
        cost: row.cost,
        costs_not_known: row.costs_not_known,
        utilization_cpu: row.utilization_cpu,
        cpu: row.utilization_cpu, // Map to cpu measure
        utilization_memory: row.utilization_memory,
        memory: row.utilization_memory, // Map to memory measure
        utilization_storage: row.utilization_storage,
        storage: row.utilization_storage, // Map to storage measure
        has_cpu_metrics: row.has_cpu_metrics,
        has_memory_metrics: row.has_memory_metrics,
        has_storage_metrics: row.has_storage_metrics,
        isGroup: false
      }));
    }

    return transformedData;
  }, [rawData, config?.displayLevel, config?.selectedClusters, config?.selectedOtherResourceTypes, groupResourceList]);


  // Define measures based on displayLevel and visibleColumns
  const selectedMeasures = useMemo(() => {
    const displayLevel = config?.displayLevel || 'first';
    
    if (displayLevel === 'first') {
      // First level: only show specific columns
      return ['units', 'cpu', 'memory', 'storage', 'vendor'];
    } else {
      // Second level: use configured column visibility and order
      const measures = [];
      
      // Always include resource_name as grouping (handled by TableComponent)
      // Add measures based on visibleColumns and optionalColumnOrder
      for (const colId of optionalColumnOrder) {
        if (visibleColumns[colId]) {
          measures.push(colId);
        }
      }
      
      return measures;
    }
  }, [config?.displayLevel, visibleColumns, optionalColumnOrder]);



  // Determine the empty state content
  const emptyStateContent = useMemo(() => {
    const displayLevel = config?.displayLevel || 'first';
    const selectedClusters = config?.selectedClusters || [];
    const selectedOtherResourceTypes = config?.selectedOtherResourceTypes || [];
    
    if (!rawData || rawData.length === 0) {
      return {
        title: 'No resources available',
        description: 'Try adjusting your filters or date range',
        icon: serverIcon
      };
    }
    
    if (displayLevel === 'second') {
      if (selectedClusters.length === 0) {
        return {
          title: 'No resource clusters selected',
          description: 'Please select resource clusters in the widget configuration',
          icon: folderOpenIcon
        };
      }
      
      if (selectedClusters.includes('Other') && (!selectedOtherResourceTypes || selectedOtherResourceTypes.length === 0)) {
        return {
          title: 'No resource types selected',
          description: 'Please select resource types for "Other" cluster in the widget configuration',
          icon: folderOpenIcon
        };
      }
      
      return {
        title: 'No resources match filters',
        description: 'Try adjusting your selected clusters or filters',
        icon: filterIcon
      };
    }
    
    return {
      title: 'No resources available',
      description: 'Try adjusting your filters or date range',
      icon: serverIcon
    };
  }, [rawData, config?.displayLevel, config?.selectedClusters, config?.selectedOtherResourceTypes]);

  return (
    <BaseWidget
      widgetId={widgetId}
      title={config.title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      <div className="h-full w-full overflow-auto flex flex-col relative">
        {preparedData && preparedData.length > 0 ? (
          <TableComponent
            data={preparedData}
            grouping="resource_name"
            title={config.title}
            showTitle={config.showTitle}
            measures={selectedMeasures}
            tableType="resource-list"
            showIcons={config.showIcons === true}
            showTotalRow={config.showTotalRow === true}
          />
        ) : (
          <WidgetEmptyState 
            title={emptyStateContent.title}
            description={emptyStateContent.description}
            icon={emptyStateContent.icon}
          />
        )}
      </div>
    </BaseWidget>
  );
};

ResourceListWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    showTitle: PropTypes.bool,
    showIcons: PropTypes.bool,
    showTotalRow: PropTypes.bool,
    displayLevel: PropTypes.oneOf(['first', 'second']),
    selectedClusters: PropTypes.arrayOf(PropTypes.string),
    selectedOtherResourceTypes: PropTypes.arrayOf(PropTypes.string),
    visibleColumns: PropTypes.object,
    visibleColumnsOrder: PropTypes.arrayOf(PropTypes.string),
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool
};

export default ResourceListWidget;
